import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Switch,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import PersianDatePicker from '../PersianDatePicker';
import { financeSettingsApi, ValidationError, clientsApi, parse422Error } from '../../utils/api';
import { formatMoneyFa as formatMoney } from '../../utils/formatPriceToman';
import { formatJalaliApiDate } from '../../utils/dateUtils';
import { applyValidationErrorsToForm } from '../../utils/formHelpers';
import {
  calculateItemAmount,
  ITEM_DISCOUNT_TYPES,
} from '../../utils/itemDiscountUtils';
import { useDiscountValidation } from '../../hooks/useDiscountValidation';


const { Option } = Select;
const { TextArea } = Input;

const createEmptyItem = () => ({
  item_id: null,
  product_id: null,
  item_name: '',
  quantity: 1,
  unit_id: null,
  cost_per_item: 0,
  discount: 0,
  discount_type: 'percent',
  taxes: [],
  amount: 0,
  item_summary: '',
  invoice_item_image: null,
});

const splitInvoiceNumber = (value) => {
  const text = String(value || '');
  const match = text.match(/^(.*?)(\d+)$/);
  if (!match) return { prefix: '', number: text };
  return { prefix: match[1], number: match[2] };
};

const InvoiceForm = ({
  options,
  initialValues,
  initialItems,
  isEdit,
  submitLoading,
  onSubmit,
  lockClient = false,
  lockProject = false,
}) => {
  const [form] = Form.useForm();
  const [items, setItems] = useState(initialItems?.length ? initialItems : [createEmptyItem()]);
  const [products, setProducts] = useState(options?.products || []);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [removedImageItemIds, setRemovedImageItemIds] = useState([]);
  const [clientDeals, setClientDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(false);

  useEffect(() => {
    if (initialValues) {
      const { prefix, number } = splitInvoiceNumber(initialValues.invoice_number);
      form.setFieldsValue({
        ...initialValues,
        invoice_prefix: prefix,
        invoice_number_digits: number,
      });
    }
  }, [initialValues, form]);

  useEffect(() => {
    if (initialItems?.length) setItems(initialItems);
  }, [initialItems]);

  useEffect(() => {
    setProducts(options?.products || []);
  }, [options?.products]);

  const discountValue = Number(Form.useWatch('discount_value', form) || 0);
  const discountType = Form.useWatch('discount_type', form) || 'percent';
  const calculateTax = Form.useWatch('calculate_tax', form) || 'after_discount';
  const selectedCategory = Form.useWatch('category_id', form);
  const selectedClient = Form.useWatch('client_id', form);
  const shippingAddress = Form.useWatch('shipping_address', form);

  useEffect(() => {
    const loadCategoryProducts = async () => {
      if (!selectedCategory) {
        setProducts(options?.products || []);
        return;
      }
      setCategoryLoading(true);
      try {
        const res = await financeSettingsApi.getInvoiceCategoryProducts(selectedCategory);
        setProducts(res?.products || []);
      } catch (error) {
        message.error('خطا در دریافت محصولات دسته‌بندی');
      } finally {
        setCategoryLoading(false);
      }
    };
    loadCategoryProducts();
  }, [selectedCategory, options?.products]);

  useEffect(() => {
    if (!selectedClient) {
      setClientDeals([]);
      form.setFieldsValue({ deal_id: undefined });
      return;
    }
    let cancelled = false;
    const loadClientDeals = async () => {
      setDealsLoading(true);
      try {
        const res = await clientsApi.getClientDeals(selectedClient);
        if (cancelled) return;
        const deals = res?.data?.deals || res?.deals || [];
        setClientDeals(deals);
        const currentDealId = form.getFieldValue('deal_id');
        if (currentDealId && !deals.some((d) => String(d.id) === String(currentDealId))) {
          form.setFieldsValue({ deal_id: undefined });
        }
      } catch (error) {
        if (!cancelled) {
          setClientDeals([]);
          message.error('خطا در دریافت معاملات مشتری');
        }
      } finally {
        if (!cancelled) setDealsLoading(false);
      }
    };
    loadClientDeals();
    return () => {
      cancelled = true;
    };
  }, [selectedClient, form]);

  const taxRateMap = useMemo(() => {
    const map = {};
    (options?.taxes || []).forEach((t) => {
      map[t.id] = Number(t.rate_percent || 0);
    });
    return map;
  }, [options?.taxes]);

  const subTotal = useMemo(() => items.reduce((sum, it) => sum + Number(it.amount || 0), 0), [items]);
  const discountAmount = useMemo(() => {
    if (discountType === 'percent') return (subTotal * discountValue) / 100;
    return Math.min(subTotal, discountValue);
  }, [discountType, discountValue, subTotal]);

  const { validateDiscount, validationResult, clearValidation } = useDiscountValidation();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (discountValue > 0 && subTotal > 0) {
        let amt = undefined;
        let pct = undefined;
        if (discountType === 'percent') {
          pct = discountValue;
          amt = (subTotal * discountValue) / 100;
        } else {
          amt = discountValue;
          pct = (discountValue / subTotal) * 100;
        }
        validateDiscount(subTotal, amt, pct);
      } else {
        clearValidation();
      }
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [discountValue, discountType, subTotal, validateDiscount, clearValidation]);


  const totalTaxAmount = useMemo(() => {
    if (!items.length) return 0;
    const discountRatio = subTotal > 0 ? Math.max(0, (subTotal - discountAmount) / subTotal) : 1;
    return items.reduce((sum, item) => {
      const baseAmount = Number(item.amount || 0);
      const effectiveBase = calculateTax === 'after_discount' ? baseAmount * discountRatio : baseAmount;
      const rowTax = (item.taxes || []).reduce((taxSum, taxId) => {
        const rate = Number(taxRateMap[taxId] || 0);
        return taxSum + (effectiveBase * rate) / 100;
      }, 0);
      return sum + rowTax;
    }, 0);
  }, [items, calculateTax, subTotal, discountAmount, taxRateMap]);

  const total = useMemo(() => subTotal - discountAmount + totalTaxAmount, [subTotal, discountAmount, totalTaxAmount]);

  const setItemValue = (index, patch) => {
    setItems((prev) => {
      const next = [...prev];
      const updated = { ...next[index], ...patch };
      if (updated.discount_type === 'percent' && Number(updated.discount) > 100) {
        updated.discount = 100;
      }
      if (Number(updated.discount) < 0) {
        updated.discount = 0;
      }
      updated.amount = calculateItemAmount(updated);
      next[index] = updated;
      return next;
    });
  };

  const addItemRow = () => setItems((prev) => [...prev, createEmptyItem()]);
  const removeItemRow = (index) => setItems((prev) => prev.filter((_, i) => i !== index));
  const isProductUsedInOtherRow = (productId, rowIndex) =>
    productId != null && items.some((item, index) => index !== rowIndex && String(item.product_id) === String(productId));

  const getValidationMessage = (error) => {
    if (error instanceof ValidationError) {
      return parse422Error({ error: { details: error.details } }) || error.message;
    }
    if (error?.details) {
      return parse422Error({ error: { details: error.details } }) || error.message;
    }
    return error?.message || 'خطا در ذخیره فاکتور';
  };

  const showSubmitErrorMessage = (errorText) => {
    message.error({
      content: <span style={{ whiteSpace: 'pre-line' }}>{errorText}</span>,
      duration: 6,
    });
  };

  const handleStoreShippingAddress = async () => {
    if (!selectedClient || !shippingAddress) {
      message.warning('مشتری و آدرس ارسال باید وارد شود');
      return;
    }
    setShippingLoading(true);
    try {
      await financeSettingsApi.addInvoiceShippingAddress(selectedClient, shippingAddress);
      message.success('آدرس ارسال ذخیره شد');
    } catch (error) {
      message.error('خطا در ذخیره آدرس ارسال');
    } finally {
      setShippingLoading(false);
    }
  };

  const handleRemoveExistingImage = async (item) => {
    if (!item?.item_id) return;
    try {
      await financeSettingsApi.deleteInvoiceItemImage(item.item_id);
      setRemovedImageItemIds((prev) => [...prev, item.item_id]);
      setItems((prev) => prev.map((it) => (it.item_id === item.item_id ? { ...it, invoice_item_image: null } : it)));
      message.success('تصویر آیتم حذف شد');
    } catch (error) {
      message.error('خطا در حذف تصویر آیتم');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const invoiceNumber = `${values.invoice_prefix || ''}${values.invoice_number_digits || ''}`;
      // محصول اختیاری است؛ آیتم دستی فقط با نام و تعداد معتبر است و product_id = null ارسال می‌شود
      const validItems = items.filter((it) => it.item_name && Number(it.quantity) > 0);
      if (!validItems.length) {
        message.warning('حداقل یک آیتم معتبر وارد کنید');
        return;
      }

      const duplicateProductId = validItems
        .map((item) => item.product_id)
        .filter((productId) => productId != null)
        .find((productId, index, arr) => arr.findIndex((id) => String(id) === String(productId)) !== index);
      if (duplicateProductId) {
        const productName = products.find((product) => String(product.id) === String(duplicateProductId))?.name || 'انتخاب‌شده';
        const errorText = `محصول «${productName}» بیش از یک بار در فاکتور انتخاب شده است.`;
        message.warning(errorText);
        return;
      }

      const payload = {
        do_it_later: 'direct',
        invoice_number: invoiceNumber,
        issue_date: formatJalaliApiDate(values.issue_date),
        due_date: formatJalaliApiDate(values.due_date),
        currency_id: values.currency_id,
        client_id: values.client_id,
        deal_id: values.deal_id || null,
        project_id: values.project_id || null,
        calculate_tax: values.calculate_tax || 'after_discount',
        bank_account_id: values.bank_account_id || null,
        invoice_payment_id: values.invoice_payment_id || null,
        billing_address: null,
        shipping_address: values.shipping_address || '',
        company_address_id: values.company_address_id || null,
        category_id: values.category_id || null,
        sort_order: validItems.map((it) => it.product_id ?? null),
        item_name: validItems.map((it) => it.item_name),
        quantity: validItems.map((it) => Number(it.quantity || 0)),
        product_id: validItems.map((it) => it.product_id ?? null),
        unit_id: validItems.map((it) => it.unit_id || null),
        cost_per_item: validItems.map((it) => Number(it.cost_per_item || 0)),
        item_discount: validItems.map((it) => Number(it.discount || 0)),
        item_discount_type: validItems.map((it) => it.discount_type || 'percent'),
        taxes: validItems.map((it) => it.taxes || []),
        amount: validItems.map((it) => Number(it.amount || 0)),
        item_summary: validItems.map((it) => it.item_summary || ''),
        invoice_item_image_url: [],
        invoice_item_image: [],
        sub_total: Number(subTotal.toFixed(2)),
        discount_value: Number(values.discount_value || 0),
        discount_type: values.discount_type || 'percent',
        total: Number(total.toFixed(2)),
        note: values.note || '',
        payment_status: values.payment_status ? 1 : 0,
        gateway: values.payment_status ? (values.gateway || 'Offline') : null,
        offline_methods: values.payment_status ? (values.offline_methods || null) : null,
        transaction_id: values.payment_status ? (values.transaction_id || '') : '',
        show_shipping_address: values.show_shipping_address ? 'yes' : 'no',
      };

      if (isEdit) {
        payload.item_ids = validItems.map((it) => it.item_id).filter(Boolean);
        payload.removed_item_images = removedImageItemIds;
      }

      await onSubmit(payload);
    } catch (error) {
      if (error?.errorFields?.length) {
        const firstField = error.errorFields[0]?.name;
        if (firstField) {
          form.scrollToField(firstField, { behavior: 'smooth', block: 'center' });
        }
        return;
      }
      if (error instanceof ValidationError || error?.details) {
        showSubmitErrorMessage(getValidationMessage(error));
        applyValidationErrorsToForm(form, error);
      } else {
        const errorText = getValidationMessage(error);
        showSubmitErrorMessage(errorText);
      }
    }
  };

  const invoicePayments = options?.invoicePayment || [];
  const bankAccounts = options?.bankAccounts || [];
  const clients = options?.clients || [];
  const currencies = options?.currencies || [];
  const categories = options?.categories || [];
  const units = options?.units || [];
  const taxes = options?.taxes || [];
  const companyAddresses = options?.companyAddresses || [];
  const offlinePayments = options?.offlinePayments || [];

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
      style={{ fontFamily: 'CustomFont' }}
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="grid grid-cols-12 gap-2">
          <Form.Item name="invoice_prefix" label="پیشوند" className="col-span-5">
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="invoice_number_digits"
            label="شماره فاکتور"
            className="col-span-7"
            rules={[{ required: true, message: 'شماره فاکتور الزامی است' }]}
          >
            <Input disabled={isEdit} dir="ltr" />
          </Form.Item>
        </div>
        <Form.Item name="issue_date" label="تاریخ صدور" rules={[{ required: true, message: 'الزامی است' }]}>
          <PersianDatePicker />
        </Form.Item>
        <Form.Item name="due_date" label="تاریخ سررسید" rules={[{ required: true, message: 'الزامی است' }]}>
          <PersianDatePicker />
        </Form.Item>
        <Form.Item name="currency_id" label="واحد پول" rules={[{ required: true, message: 'الزامی است' }]}>
          <Select>{currencies.map((c) => <Option key={c.id} value={c.id}>{c.currency_symbol}</Option>)}</Select>
        </Form.Item>
        <Form.Item name="client_id" label="مشتری">
          <Select showSearch optionFilterProp="label" disabled={lockClient} style={{ fontFamily: 'CustomFont' }}>
            {clients.map((c) => {
              const name = c.name_salutation || c.name || '—';
              const mobile = c.mobile || c.cell || c.phone || c.client_details?.mobile || '';
              const label = mobile ? `${name} — ${mobile}` : name;
              return (
                <Option key={c.id} value={c.id} label={label}>
                  {label}
                </Option>
              );
            })}
          </Select>
        </Form.Item>
        <Form.Item name="deal_id" label="معامله">
          <Select
            allowClear
            loading={dealsLoading}
            disabled={!selectedClient}
            placeholder={selectedClient ? 'انتخاب معامله (اختیاری)' : 'ابتدا مشتری را انتخاب کنید'}
            showSearch
            optionFilterProp="label"
            options={clientDeals.map((d) => ({ value: d.id, label: d.name }))}
          />
        </Form.Item>
        <Form.Item name="project_id" label="پروژه">
          <Select allowClear={!lockProject} disabled={lockProject}>
            {(options?.projects || []).map((p) => <Option key={p.id} value={p.id}>{p.project_name}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="bank_account_id" label="حساب بانکی">
          <Select allowClear>
            {bankAccounts.map((b) => <Option key={b.id} value={b.id}>{b.account_name} {b.bank_name ? `(${b.bank_name})` : ''}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="invoice_payment_id" label="جزئیات پرداخت">
          <Select allowClear>
            {invoicePayments.map((p) => <Option key={p.id} value={p.id}>{p.title}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="company_address_id" label="آدرس شرکت">
          <Select allowClear>
            {companyAddresses.map((a) => <Option key={a.id} value={a.id}>{a.location}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="category_id" label="دسته‌بندی">
          <Select allowClear loading={categoryLoading}>
            {categories.map((c) => <Option key={c.id} value={c.id}>{c.category_name}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="calculate_tax" label="محاسبه مالیات">
          <Select>
            <Option value="after_discount">بعد از تخفیف</Option>
            <Option value="before_discount">قبل از تخفیف</Option>
          </Select>
        </Form.Item>
        <Form.Item name="discount_value" label="تخفیف کلی فاکتور">
          <InputNumber className="w-full" min={0} />
        </Form.Item>
        <Form.Item name="discount_type" label="نوع تخفیف کلی">
          <Select>
            <Option value="percent">درصدی</Option>
            <Option value="fixed">ثابت</Option>
          </Select>
        </Form.Item>
        <div className="col-span-full">
          {validationResult && validationResult.allowed === false && (
            <div className="text-red-500 bg-red-50 p-3 rounded-lg text-sm border border-red-200">
              {validationResult.message || 'درصد تخفیف درخواستی از سقف مجاز نقش سازمانی شما فراتر است.'}
            </div>
          )}
        </div>
        <Form.Item name="show_shipping_address" label="نمایش آدرس ارسال" valuePropName="checked">
          <Switch checkedChildren="بله" unCheckedChildren="خیر" />
        </Form.Item>
      </div>

      <Form.Item name="shipping_address" label="آدرس ارسال">
        <TextArea rows={2} />
      </Form.Item>
      <div className="mb-3">
        <Button onClick={handleStoreShippingAddress} loading={shippingLoading}>
          ذخیره آدرس ارسال مشتری
        </Button>
      </div>

      <Card size="small" title="پرداخت اولیه (اختیاری)" className="mb-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Form.Item name="payment_status" label="ثبت پرداخت اولیه" valuePropName="checked">
            <Switch checkedChildren="بله" unCheckedChildren="خیر" />
          </Form.Item>
          <Form.Item name="gateway" label="درگاه">
            <Select allowClear>
              <Option value="Offline">Offline</Option>
            </Select>
          </Form.Item>
          <Form.Item name="offline_methods" label="روش آفلاین">
            <Select allowClear>
              {offlinePayments.map((m) => <Option key={m.id} value={m.id}>{m.name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="transaction_id" label="شماره تراکنش">
            <Input />
          </Form.Item>
        </div>
      </Card>

      <Card size="small" title="آیتم‌ها" className="mb-3">
        {items.map((it, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-2 p-2 border rounded dark:border-gray-600">
            <div className="md:col-span-3">
              <Select
                className="w-full"
                placeholder="محصول (اختیاری)"
                allowClear
                value={it.product_id}
                onChange={(val) => {
                  if (val == null) {
                    setItemValue(index, { product_id: null });
                    return;
                  }
                  const p = products.find((x) => x.id === val);
                  setItemValue(index, {
                    product_id: val,
                    item_name: p?.name || it.item_name,
                    cost_per_item: Number(p?.price || 0),
                    unit_id: p?.unit_id || it.unit_id,
                  });
                }}
                showSearch
                optionFilterProp="children"
              >
                {products.map((p) => (
                  <Option key={p.id} value={p.id} disabled={isProductUsedInOtherRow(p.id, index)}>
                    {p.name}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="md:col-span-2">
              <Input placeholder="نام آیتم" value={it.item_name} onChange={(e) => setItemValue(index, { item_name: e.target.value })} />
            </div>
            <div className="md:col-span-1">
              <InputNumber className="w-full" min={1} value={it.quantity} onChange={(v) => setItemValue(index, { quantity: Number(v || 0) })} placeholder="تعداد" />
            </div>
            <div className="md:col-span-1">
              <Select className="w-full" placeholder="واحد" value={it.unit_id} onChange={(v) => setItemValue(index, { unit_id: v })}>
                {units.map((u) => <Option key={u.id} value={u.id}>{u.unit_type}</Option>)}
              </Select>
            </div>
            <div className="md:col-span-2">
              <InputNumber
                className="w-full"
                min={0}
                value={it.cost_per_item}
                onChange={(v) => setItemValue(index, { cost_per_item: Number(v || 0) })}
                placeholder="قیمت واحد"
              />
            </div>
            <div className="md:col-span-1">
              <InputNumber
                className="w-full"
                min={0}
                max={it.discount_type === 'percent' ? 100 : undefined}
                value={it.discount}
                onChange={(v) => setItemValue(index, { discount: Number(v || 0) })}
                placeholder="تخفیف"
              />
            </div>
            <div className="md:col-span-1">
              <Select
                className="w-full"
                value={it.discount_type || 'percent'}
                onChange={(v) => setItemValue(index, { discount_type: v })}
              >
                {ITEM_DISCOUNT_TYPES.map((t) => (
                  <Option key={t.value} value={t.value}>{t.label}</Option>
                ))}
              </Select>
            </div>
            <div className="md:col-span-1 flex items-center gap-1">
              <Popconfirm title="حذف آیتم؟" onConfirm={() => removeItemRow(index)} okText="بله" cancelText="خیر">
                <Button danger icon={<DeleteOutlined />} disabled={items.length === 1} />
              </Popconfirm>
            </div>
            <div className="md:col-span-3">
              <Select
                mode="multiple"
                className="w-full"
                placeholder="مالیات"
                value={it.taxes}
                onChange={(v) => setItemValue(index, { taxes: v })}
              >
                {taxes.map((t) => <Option key={t.id} value={t.id}>{t.tax_name} ({t.rate_percent}%)</Option>)}
              </Select>
            </div>
            <div className="md:col-span-3 flex items-center text-xs text-slate-500 tabular-nums">
              مبلغ ردیف: <b className="mr-1 text-slate-800 dark:text-slate-100">{formatMoney(it.amount)}</b>
            </div>
            <div className="md:col-span-12">
              <Input placeholder="خلاصه آیتم" value={it.item_summary} onChange={(e) => setItemValue(index, { item_summary: e.target.value })} />
            </div>
            {it.invoice_item_image && (
              <div className="md:col-span-12">
                <Button danger size="small" onClick={() => handleRemoveExistingImage(it)}>
                  حذف تصویر آیتم
                </Button>
              </div>
            )}
          </div>
        ))}
        <Button icon={<PlusOutlined />} onClick={addItemRow}>افزودن آیتم</Button>
      </Card>

      <Card size="small" className="mb-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
          <div>جمع پس از تخفیف آیتم‌ها: <b>{formatMoney(subTotal)}</b></div>
          <div>تخفیف کلی فاکتور: <b>{formatMoney(discountAmount)}</b></div>
          <div>مالیات: <b>{formatMoney(totalTaxAmount)}</b></div>
          <div className="md:col-span-3">مبلغ نهایی: <b>{formatMoney(total)}</b></div>
        </div>
      </Card>

      <Form.Item name="note" label="یادداشت">
        <TextArea rows={2} />
      </Form.Item>

      <div className="flex justify-end gap-2">
        <Button onClick={() => window.history.back()}>انصراف</Button>
        <Button type="primary" htmlType="submit" className="bg-green-600 hover:bg-green-700 border-0" loading={submitLoading}>
          {isEdit ? 'ذخیره تغییرات' : 'ایجاد فاکتور'}
        </Button>
      </div>
    </Form>
  );
};

export default InvoiceForm;
